import React, { useState, useEffect } from 'react';
import { livpureMockData } from '../data';

// Import analytics components
import AnalyticsFilters from '../components/analytics/AnalyticsFilters';
import TrendChart from '../components/analytics/TrendChart';
import SentimentSummary from '../components/analytics/SentimentSummary';
import PlatformDistribution from '../components/analytics/PlatformDistribution';
import RatingDistribution from '../components/analytics/RatingDistribution';
import HierarchyTable from '../components/analytics/HierarchyTable';
import CityViewTable from '../components/analytics/CityViewTable';



// Generate NPS data once
const npsData = livpureMockData.generateNpsData();

const ReviewAnalytics = () => {
  // Get current date for default date range (last 7 days)
  const today = new Date();
  const thrityDaysAgo = new Date();
  thrityDaysAgo.setDate(today.getDate() - 30);
  
  // Filter states
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState('global');
  const [selectedCity, setSelectedCity] = useState('');
  const [dateRange, setDateRange] = useState([thrityDaysAgo, today]); // Default to last 7 days
  const [selectedProduct, setSelectedProduct] = useState('');
  const [selectedAppStore, setSelectedAppStore] = useState('');
  
  // Data type states
  const [showLocations, setShowLocations] = useState(true);
  const [showProducts, setShowProducts] = useState(true);
  const [showApps, setShowApps] = useState(true);
  const [isNpsSelected, setIsNpsSelected] = useState(true);
  
  const [filteredData, setFilteredData] = useState([]);
  
  // Combine reviews with NPS data
  const allData = React.useMemo(() => {
    return [...livpureMockData.reviews, ...npsData];
  }, []);
  
  // Sample products list
  const PRODUCTS = [
    "Product Original", 
    "Product Premium", 
    "Product Max", 
    "Product Everyday", 
    "Product Complete"
  ];

  // Assign products to existing data if missing
  const enrichDataWithProducts = React.useMemo(() => {
    return allData.map(review => {
      if (!review.product) {
        // Assign a consistent product based on review ID to ensure stability
        const productIndex = review.id % PRODUCTS.length;
        return {
          ...review,
          product: PRODUCTS[productIndex],
          reviewText: review.reviewText + ` (Product: ${PRODUCTS[productIndex]})`
        };
      }
      return review;
    });
  }, [allData]);

  // Data filtering logic
  useEffect(() => {
    let data = [...enrichDataWithProducts];
    
    // Filter by data types
    const selectedSources = [];
    if (showLocations) selectedSources.push('Google Reviews');
    if (showProducts) selectedSources.push('Amazon');
    if (showApps) selectedSources.push('App Store', 'Play Store');
    if (isNpsSelected) selectedSources.push('NPS');
    if (selectedSources.length > 0) {
      data = data.filter(review => selectedSources.includes(review.source));
    }
    
    // Apply region filter
    if (selectedRegion !== 'global') {
      data = data.filter(review => review.region === selectedRegion);
      
      // Apply city filter if region is selected
      if (selectedCity) {
        data = data.filter(review => review.city === selectedCity);
      }
    }
    
    // Apply product filter
    // if (selectedProduct) {
    //   data = data.filter(review => review.product === selectedProduct);
    // }
    
    // Apply app store filter
    if (selectedAppStore) {
      if (selectedAppStore === "Both") {
        data = data.filter(review => 
          review.source === "App Store" || review.source === "Play Store"
        );
      } else {
        data = data.filter(review => review.source === selectedAppStore);
      }
    }
    
    // Apply date range filter
    if (dateRange[0]) {
      const startDate = new Date(dateRange[0]);
      startDate.setHours(0, 0, 0, 0);
      data = data.filter(review => new Date(review.date) >= startDate);
    }
    
    if (dateRange[1]) {
      const endDate = new Date(dateRange[1]);
      endDate.setHours(23, 59, 59, 999);
      data = data.filter(review => new Date(review.date) <= endDate);
    }
    
    console.log("filtered data", data.length);
    setFilteredData(data);
  }, [
    selectedRegion, 
    selectedCity, 
    dateRange, 
    showLocations, 
    showProducts, 
    showApps, 
    isNpsSelected, 
    selectedProduct, 
    selectedAppStore,
    enrichDataWithProducts
  ]);
  
  // Toggle filters expanded state
  const toggleFiltersExpand = () => {
    setIsFiltersExpanded(!isFiltersExpanded);
  };
  
  // Check if we have any data to display
  const noDataTypesSelected = !showLocations && !showProducts && !showApps && !isNpsSelected;
  
  return (
    <div>
      <div className="mb-3">
        <h1 className="text-2xl font-bold text-gray-900">Review Analytics</h1>
        <p className="text-sm text-gray-600">Analyze review trends, sentiment, and distribution across channels</p>
      </div>
      
      {/* Filters with radio buttons for data types */}
      <AnalyticsFilters 
        regions={livpureMockData.regions}
        selectedRegion={selectedRegion}
        setSelectedRegion={setSelectedRegion}
        selectedCity={selectedCity}
        setSelectedCity={setSelectedCity}
        dateRange={dateRange}
        setDateRange={setDateRange}
        showLocations={showLocations}
        setShowLocations={setShowLocations}
        showProducts={showProducts}
        setShowProducts={setShowProducts}
        showApps={showApps}
        setShowApps={setShowApps}
        showNPS={isNpsSelected}
        setShowNPS={setIsNpsSelected}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        selectedAppStore={selectedAppStore}
        setSelectedAppStore={setSelectedAppStore}
        isExpanded={isFiltersExpanded}
        toggleExpand={toggleFiltersExpand}
      />
      
      {filteredData.length === 0 || noDataTypesSelected ? (
        // No data message
        <div className="bg-white rounded-lg shadow-sm p-12 mb-6 text-center">
          <svg className="mx-auto h-16 w-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
          <h3 className="text-lg font-medium text-gray-900 mb-2">No data to display</h3>
          {noDataTypesSelected ? (
            <p className="text-gray-500 mb-4">Please select at least one data type (Locations, Products, Apps, or NPS).</p>
          ) : (
            <p className="text-gray-500 mb-4">Try adjusting your filters to see more data.</p>
          )}
          <button
            onClick={() => {
              setSelectedRegion('global');
              setSelectedCity('');
              setDateRange([thrityDaysAgo, today]);
              setSelectedProduct('');
              setSelectedAppStore('');
              setShowLocations(true);
              setShowProducts(true);
              setShowApps(true);
              setIsNpsSelected(false);
            }}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Reset All Filters
          </button>
        </div>
      ) : (
        <>
          {/* Main Content */}
          {/* Volume Trend Chart - First component as requested */}
          <TrendChart data={filteredData} />
          
          {/* Sentiment Summary with Pie Chart */}
          <SentimentSummary data={filteredData} />
          
          {/* Distribution components in a grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <PlatformDistribution 
              data={filteredData} 
              isSingleCity={selectedCity !== ''}
            />
            <RatingDistribution data={filteredData} />
          </div>
          
          {/* Hierarchical/City Data Viewer - switch based on whether a city is selected */}
          <div className="bg-white rounded-lg shadow-sm p-4 mb-6">
            {selectedCity ? (
              <CityViewTable data={filteredData} city={selectedCity} />
            ) : (
              <>
                <h2 className="text-lg font-medium text-gray-900 mb-4">Hierarchical Review Distribution</h2>
                <HierarchyTable data={filteredData} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ReviewAnalytics;